import { Check, ChevronDown, Minus, Plus } from "lucide-react";
import type { CounterConfig, CounterDayData } from "@/lib/schema";
import type { WidgetDayViewProps } from "@/widgets/types";
import { useAutoCollapse } from "@/widgets/useAutoCollapse";
import { scoreDay, unitCeiling } from "./scoring";

type Props = WidgetDayViewProps<CounterConfig>;

const PREVIEW_COUNT = 3;

export function DayView({
  config,
  data,
  dayKey,
  onChangeDay,
  readOnly = false,
}: Props) {
  const realData = (data ?? { count: 0 }) as CounterDayData;
  const ceiling = unitCeiling(config);
  const showPreview = readOnly && realData.count === 0;
  // Clamped, or a 5p-per-unit counter capped at 10p would preview three units
  // past a ceiling of two.
  const count = showPreview ? Math.min(PREVIEW_COUNT, ceiling) : realData.count;
  const score = scoreDay(config, { count } satisfies CounterDayData);

  const atMax = realData.count >= ceiling;
  const finished = realData.finished === true;

  // Ferdiglogget (CONTEXT.md): the Daily maximum is this kind's finish line.
  // The cap exists so no single day can stand in for the week, which is what
  // makes logging past it worthless and reaching it an effort claim rather
  // than an achievement one (docs/adr/0017). «Ferdig for i dag» claims the
  // same state early and, unlike meal-log's marker of that name, gates no
  // points and destroys nothing.
  const loggingCompleted = !readOnly && (atMax || finished);

  const { open, toggle } = useAutoCollapse(dayKey, loggingCompleted);
  // Collapsible at all times like meal-log, not only once the card is green —
  // an ordinary counter is foldable too. The settings preview never collapses.
  const collapsible = !readOnly;
  const collapsed = collapsible && !open;

  const step = (delta: number) => {
    if (readOnly) return;
    // Clamped upward only: a day carrying a count from before the ceiling
    // existed walks down a step at a time rather than snapping onto it.
    const raw = realData.count + delta;
    const next = delta > 0 ? Math.min(ceiling, raw) : Math.max(0, raw);
    if (next === realData.count) return;
    // Touching the tally is the one act that contradicts «Ferdig for i dag»,
    // so any edit drops the marker — a correction downward included. Omitting
    // the key is the clearing: absent means false.
    onChangeDay({ count: next } satisfies CounterDayData);
  };

  const toggleFinished = () => {
    if (readOnly) return;
    onChangeDay({
      count: realData.count,
      finished: !finished,
    } satisfies CounterDayData);
  };

  return (
    <section
      className={`widget-card counter${readOnly ? " read-only" : ""}${collapsed ? " collapsed" : ""}${loggingCompleted ? " logging-completed" : ""}`}
      data-testid={`widget-day-${config.id}`}
      data-logging-completed={loggingCompleted ? "true" : "false"}
    >
      <header className="widget-card-head">
        <h3 className="widget-card-title">{config.label}</h3>
        <div className="widget-card-head-right">
          <span
            className="widget-card-score"
            data-testid={`widget-score-${config.id}`}
          >
            <b>{score}</b>
            <span className="max"> / {config.maxPoints}p</span>
          </span>
          {collapsible ? (
            <button
              type="button"
              className="widget-card-toggle"
              onClick={toggle}
              aria-label={collapsed ? "Utvid" : "Skjul"}
              aria-expanded={!collapsed}
              data-testid={`widget-collapse-${config.id}`}
            >
              <ChevronDown className="chev" size={16} />
            </button>
          ) : null}
        </div>
      </header>
      {collapsed ? (
        <div
          className="widget-card-summary"
          data-testid={`widget-summary-${config.id}`}
        >
          {/* «maks nådd» and «ferdig» are claims, so a card folded away
              mid-count states its score instead of claiming either. */}
          <span className="summary-line">
            {loggingCompleted ? (
              <Check className="summary-icon" size={14} aria-hidden="true" />
            ) : null}
            {realData.count} ·{" "}
            {loggingCompleted ? (atMax ? "maks nådd" : "ferdig") : `${score}p`}
          </span>
        </div>
      ) : null}
      <div className="widget-card-body">
        {/* One row: the terms on the left, the stepper held to its own width
            on the right. The cap needs no separate "stopper på N" — that is
            what «maks Np per dag» now means. */}
        <div className="counter-row">
          <div className="widget-card-submeta">
            {config.points}p per enhet · maks {config.maxPoints}p per dag
          </div>
          <div className="stepper">
            <button
              type="button"
              onClick={() => step(-1)}
              disabled={readOnly || count === 0}
              aria-label="Trekk fra én"
              data-testid={`counter-decrement-button-${config.id}`}
            >
              <Minus size={14} />
            </button>
            <span className="val" data-testid={`counter-count-${config.id}`}>
              {count}
            </span>
            <button
              type="button"
              onClick={() => step(1)}
              disabled={readOnly || count >= ceiling}
              aria-label="Legg til én"
              data-testid={`counter-increment-button-${config.id}`}
            >
              <Plus size={14} />
            </button>
          </div>
        </div>
        {/* Not offered at the cap: the card is green from the count alone, so
            the button would toggle something invisible. */}
        {!readOnly && !atMax ? (
          <div className="counter-finish">
            <button
              type="button"
              className={finished ? "btn ghost" : "btn-primary"}
              onClick={toggleFinished}
              data-testid={`counter-finish-button-${config.id}`}
            >
              {finished ? "Åpne dagen igjen" : "Ferdig for i dag"}
            </button>
          </div>
        ) : null}
      </div>
    </section>
  );
}
